import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material'
import {
  Storage as DatabasesIcon,
  Public as DomainsIcon,
  People as UsersIcon,
  Person as PersonIcon,
  ChevronLeft as CollapseIcon,
  ChevronRight as ExpandIcon,
  CallSplit as BranchIcon,
  AccountCircle as AccountIcon,
  Logout as LogoutIcon,
  AdminPanelSettings,
  Palette as PaletteIcon,
  Receipt as ReceiptIcon,
  MenuBook as MenuBookIcon,
  Feedback as FeedbackIcon,
  AccountTreeOutlined as ProjectsIcon,
} from '@mui/icons-material'
import { useAuth } from '../contexts/AuthContext'
import { useProjects } from '../contexts/ProjectContext'
import { API_URL } from '../config'
import { apiClient } from '../utils/apiClient'
import VeilStreamLogo from './VeilStreamLogo'
import { getTableSortSettings } from '../utils/settings'
import {
  DATABASES_LIST_CHANGED_EVENT,
  DATABASE_USERS_LIST_CHANGED_EVENT,
} from '../utils/databasesListEvents'
import { Project } from '../types'

const SIDEBAR_WIDTH = 210
const SIDEBAR_COLLAPSED_WIDTH = 52
const PROJECTS_VISIBLE_COUNT = 5
const DATABASES_VISIBLE_COUNT = 5
const DATABASE_USERS_VISIBLE_COUNT = 5

interface SidebarDatabase {
  id: number
  name: string
}

interface SidebarDatabaseUser {
  id: number
  username: string
}

const Sidebar: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, user, logout } = useAuth()
  const { projects } = useProjects()
  const [collapsed, setCollapsed] = useState(() =>
    localStorage.getItem('sidebar-collapsed') === 'true'
  )
  const [showAllProjects, setShowAllProjects] = useState(false)
  const [projectSortKey, setProjectSortKey] = useState(() => getTableSortSettings('projects', 'projects-list'))
  const [projectEnvironments, setProjectEnvironments] = useState<any[]>([])
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null)
  /** When URL is `/deployment/:id`, resolved from GET /api/deployments/:id for sidebar project tree. */
  const [deploymentNavContext, setDeploymentNavContext] = useState<{
    projectId: string
    envId: string
  } | null>(null)

  const [databases, setDatabases] = useState<SidebarDatabase[]>([])
  const [showAllDatabases, setShowAllDatabases] = useState(false)
  const [sidebarDbUsers, setSidebarDbUsers] = useState<SidebarDatabaseUser[]>([])
  const [showAllDbUsers, setShowAllDbUsers] = useState(false)

  const showProjectTree =
    location.pathname.startsWith('/project') ||
    /^\/deployment\/\d+/.test(location.pathname)
  const isDomainsActive = location.pathname === '/domains' || location.pathname.startsWith('/domains/')
  const isProjectsExact = location.pathname === '/projects'
  const showDomainsNav = user != null

  const envIdMatch = location.pathname.match(/\/environment\/(\d+)/)
  const envIdFromPath = envIdMatch ? envIdMatch[1] : null

  const projectIdMatch = location.pathname.match(/^\/project\/(\d+)/)
  const projectIdFromPath = projectIdMatch ? projectIdMatch[1] : null

  const deploymentRouteMatch = location.pathname.match(/^\/deployment\/(\d+)/)
  const deploymentIdFromPath = deploymentRouteMatch ? deploymentRouteMatch[1] : null

  const sidebarProjectIdStr = projectIdFromPath ?? deploymentNavContext?.projectId ?? null
  const sidebarEnvIdStr = envIdFromPath ?? deploymentNavContext?.envId ?? null

  const databaseIdMatch = location.pathname.match(/^\/databases\/(\d+)/)
  const sidebarDatabaseIdStr = databaseIdMatch ? databaseIdMatch[1] : null
  const databaseUserIdMatch = location.pathname.match(/^\/databases\/user\/(\d+)/)
  const sidebarDbUserIdStr = databaseUserIdMatch ? databaseUserIdMatch[1] : null
  /** Same idea as Projects: stay under the whole Databases area (list, a DB, users, roles, etc.). */
  const showDatabaseTree = location.pathname.startsWith('/databases')
  /** Individual database shortcuts; hidden on org-wide pages so Users (etc.) is the clear focus. */
  const showDatabaseInstanceList =
    location.pathname === '/databases' ||
    location.pathname === '/databases/' ||
    /^\/databases\/\d+/.test(location.pathname)
  const showDatabaseUserList =
    location.pathname === '/databases/users' || /^\/databases\/user\/\d+/.test(location.pathname)

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'settings.json') {
        setProjectSortKey(getTableSortSettings('projects', 'projects-list'))
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  useEffect(() => {
    if (!deploymentIdFromPath) {
      setDeploymentNavContext(null)
      return
    }
    let cancelled = false
    const token = localStorage.getItem('github_access_token')
    fetch(`${API_URL}/api/deployments/${deploymentIdFromPath}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: { environment?: { id?: number; project_id?: number } } | null) => {
        if (cancelled) return
        if (!data?.environment) {
          setDeploymentNavContext(null)
          return
        }
        const pid = data.environment.project_id
        const eid = data.environment.id
        if (pid != null && eid != null) {
          setDeploymentNavContext({ projectId: String(pid), envId: String(eid) })
        } else {
          setDeploymentNavContext(null)
        }
      })
      .catch(() => {
        if (!cancelled) setDeploymentNavContext(null)
      })
    return () => {
      cancelled = true
    }
  }, [deploymentIdFromPath])

  useEffect(() => {
    if (!deploymentNavContext?.projectId) return
    const id = Number(deploymentNavContext.projectId)
    const idx = projects.findIndex((p) => p.id === id)
    if (idx !== -1 && idx >= PROJECTS_VISIBLE_COUNT) setShowAllProjects(true)
  }, [deploymentNavContext?.projectId, projects])

  useEffect(() => {
    if (!sidebarDatabaseIdStr) return
    const id = Number(sidebarDatabaseIdStr)
    const idx = databases.findIndex((d) => d.id === id)
    if (idx !== -1 && idx >= DATABASES_VISIBLE_COUNT) setShowAllDatabases(true)
  }, [sidebarDatabaseIdStr, databases])

  useEffect(() => {
    if (!showDatabaseUserList) {
      setShowAllDbUsers(false)
    }
  }, [showDatabaseUserList])

  useEffect(() => {
    if (!sidebarDbUserIdStr) return
    const id = Number(sidebarDbUserIdStr)
    const sorted = [...sidebarDbUsers].sort((a, b) => a.username.localeCompare(b.username))
    const idx = sorted.findIndex((u) => u.id === id)
    if (idx !== -1 && idx >= DATABASE_USERS_VISIBLE_COUNT) setShowAllDbUsers(true)
  }, [sidebarDbUserIdStr, sidebarDbUsers])

  useEffect(() => {
    if (!isAuthenticated || !showDatabaseTree) return
    let cancelled = false
    const loadDatabases = () => {
      apiClient
        .get<SidebarDatabase[]>('/api/databases')
        .then((data) => {
          if (!cancelled) setDatabases(Array.isArray(data) ? data : [])
        })
        .catch(() => {
          if (!cancelled) setDatabases([])
        })
    }
    loadDatabases()
    const onListChanged = () => {
      if (!cancelled) loadDatabases()
    }
    window.addEventListener(DATABASES_LIST_CHANGED_EVENT, onListChanged)
    return () => {
      cancelled = true
      window.removeEventListener(DATABASES_LIST_CHANGED_EVENT, onListChanged)
    }
  }, [isAuthenticated, showDatabaseTree])

  useEffect(() => {
    if (!isAuthenticated || !showDatabaseUserList) return
    let cancelled = false
    const loadDbUsers = () => {
      apiClient
        .get<SidebarDatabaseUser[]>('/api/databases/users')
        .then((data) => {
          if (!cancelled) setSidebarDbUsers(Array.isArray(data) ? data : [])
        })
        .catch(() => {
          if (!cancelled) setSidebarDbUsers([])
        })
    }
    loadDbUsers()
    const onUsersChanged = () => {
      if (!cancelled) loadDbUsers()
    }
    window.addEventListener(DATABASE_USERS_LIST_CHANGED_EVENT, onUsersChanged)
    return () => {
      cancelled = true
      window.removeEventListener(DATABASE_USERS_LIST_CHANGED_EVENT, onUsersChanged)
    }
  }, [isAuthenticated, showDatabaseUserList])

  useEffect(() => {
    if (!sidebarProjectIdStr) {
      setProjectEnvironments([])
      return
    }
    const fetchProjectEnvironments = () => {
      const token = localStorage.getItem('github_access_token')
      fetch(`${API_URL}/api/projects/${sidebarProjectIdStr}/environments`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.ok ? r.json() : [])
        .then(data => setProjectEnvironments(Array.isArray(data) ? data : []))
        .catch(() => setProjectEnvironments([]))
    }
    fetchProjectEnvironments()
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (String(detail?.projectId) === String(sidebarProjectIdStr)) fetchProjectEnvironments()
    }
    window.addEventListener('veilstream:project-environments-changed', handler)
    return () => window.removeEventListener('veilstream:project-environments-changed', handler)
  }, [sidebarProjectIdStr])

  if (!isAuthenticated) return null

  const toggleCollapsed = () => {
    setCollapsed(c => {
      const next = !c
      localStorage.setItem('sidebar-collapsed', String(next))
      return next
    })
  }

  // ── Style helpers ───────────────────────────────────────────────────────────

  const itemSx = (active: boolean, py = 0.5) => ({
    borderRadius: 1,
    mb: 0.25,
    py,
    color: active ? 'var(--palette-primary-0)' : 'rgba(255,255,255,0.82)',
    bgcolor: active ? 'rgba(255,255,255,0.1)' : 'transparent',
    '&:hover': {
      bgcolor: active ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)',
      color: 'rgba(255,255,255,0.96)',
    },
  })

  const iconSx = (active: boolean, minWidth = 36) => ({
    minWidth: collapsed ? 0 : minWidth,
    color: active ? 'var(--palette-primary-400)' : 'rgba(255,255,255,0.7)',
    justifyContent: 'center',
  })

  // ── Section label (not clickable, hidden when collapsed) ────────────────────

  const SectionLabel = ({ label }: { label: string }) =>
    collapsed ? null : (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: 'rgba(255,255,255,0.52)',
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          fontSize: '0.65rem',
          px: 1,
          pt: 1.25,
          pb: 0.25,
          userSelect: 'none',
        }}
      >
        {label}
      </Typography>
    )

  // ── Generic nav item ────────────────────────────────────────────────────────

  const renderItem = (
    text: string,
    icon: React.ReactNode,
    active: boolean,
    onClick: () => void,
    pl = 1,
    fontSize = '0.875rem',
  ) => {
    const button = (
      <ListItemButton
        sx={{
          ...itemSx(active),
          pl: collapsed ? 1 : pl,
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 1 : undefined,
        }}
        onClick={onClick}
      >
        <ListItemIcon sx={iconSx(active)}>{icon}</ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={text}
            primaryTypographyProps={{ fontSize, fontWeight: active ? 600 : 400, noWrap: true }}
          />
        )}
      </ListItemButton>
    )
    return collapsed ? (
      <Tooltip key={text} title={text} placement="right">{button}</Tooltip>
    ) : (
      <span key={text}>{button}</span>
    )
  }

  // ── Databases ───────────────────────────────────────────────────────────────

  const isDatabaseActive = (path: string) => {
    if (path === '/databases') return location.pathname === '/databases' || location.pathname === '/databases/'
    return location.pathname.startsWith(path)
  }

  const isDatabasesListActive =
    location.pathname === '/databases' || location.pathname === '/databases/'

  const sortedDatabases = [...databases].sort((a, b) => a.name.localeCompare(b.name))
  const visibleDatabases = showAllDatabases ? sortedDatabases : sortedDatabases.slice(0, DATABASES_VISIBLE_COUNT)
  const hasMoreDatabases = databases.length > DATABASES_VISIBLE_COUNT

  const databaseInitials = (name: string) => {
    const cleaned = (name || '').replace(/\s+/g, '')
    if (cleaned.length >= 2) return cleaned.slice(0, 2).toUpperCase()
    return (cleaned[0] || '?').toUpperCase()
  }

  const renderDatabaseList = () => {
    if (databases.length === 0) return null
    const activeDatabaseId = sidebarDatabaseIdStr ? Number(sidebarDatabaseIdStr) : null
    return (
      <>
        {visibleDatabases.map((db) => {
          const active = activeDatabaseId === db.id
          const dbRoot = `/databases/${db.id}`
          const button = (
            <ListItemButton
              sx={{
                ...itemSx(active, 0.4),
                pl: collapsed ? 1 : 2.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : undefined,
              }}
              onClick={() => navigate(dbRoot)}
            >
              {collapsed ? (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: active ? 'var(--palette-primary-400)' : 'rgba(255,255,255,0.15)',
                    color: active ? 'var(--palette-primary-0)' : 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  {databaseInitials(db.name)}
                </Box>
              ) : (
                <>
                  <ListItemIcon sx={iconSx(active)}><DatabasesIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={db.name}
                    primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 400, noWrap: true }}
                  />
                </>
              )}
            </ListItemButton>
          )
          return (
            <React.Fragment key={db.id}>
              {collapsed ? (
                <Tooltip title={db.name} placement="right">
                  {button}
                </Tooltip>
              ) : (
                <span>{button}</span>
              )}
            </React.Fragment>
          )
        })}
        {!collapsed && hasMoreDatabases && (
          <Box
            onClick={() => setShowAllDatabases((v) => !v)}
            sx={{
              pl: 2.5,
              py: 0.25,
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.72rem',
              '&:hover': { color: 'rgba(255,255,255,0.82)' },
              userSelect: 'none',
            }}
          >
            {showAllDatabases ? '↑ show less' : `+ ${databases.length - DATABASES_VISIBLE_COUNT} more`}
          </Box>
        )}
      </>
    )
  }

  const sortedDbUsers = [...sidebarDbUsers].sort((a, b) => a.username.localeCompare(b.username))
  const visibleDbUsers = showAllDbUsers ? sortedDbUsers : sortedDbUsers.slice(0, DATABASE_USERS_VISIBLE_COUNT)
  const hasMoreDbUsers = sidebarDbUsers.length > DATABASE_USERS_VISIBLE_COUNT

  const renderDatabaseUserList = () => {
    if (sidebarDbUsers.length === 0) return null
    const activeUserId = sidebarDbUserIdStr ? Number(sidebarDbUserIdStr) : null
    return (
      <>
        {visibleDbUsers.map((u) => {
          const active = activeUserId === u.id
          const path = `/databases/user/${u.id}`
          const button = (
            <ListItemButton
              key={u.id}
              sx={{
                ...itemSx(active, 0.4),
                pl: collapsed ? 1 : 2.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : undefined,
              }}
              onClick={() => navigate(path)}
            >
              {collapsed ? (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: active ? 'var(--palette-primary-400)' : 'rgba(255,255,255,0.15)',
                    color: active ? 'var(--palette-primary-0)' : 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  {databaseInitials(u.username)}
                </Box>
              ) : (
                <>
                  <ListItemIcon sx={iconSx(active)}><PersonIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={u.username}
                    primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 400, noWrap: true }}
                  />
                </>
              )}
            </ListItemButton>
          )
          return collapsed ? (
            <Tooltip key={u.id} title={u.username} placement="right">
              {button}
            </Tooltip>
          ) : (
            <span key={u.id}>{button}</span>
          )
        })}
        {!collapsed && hasMoreDbUsers && (
          <Box
            onClick={() => setShowAllDbUsers((v) => !v)}
            sx={{
              pl: 2.5,
              py: 0.25,
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.72rem',
              '&:hover': { color: 'rgba(255,255,255,0.82)' },
              userSelect: 'none',
            }}
          >
            {showAllDbUsers ? '↑ show less' : `+ ${sidebarDbUsers.length - DATABASE_USERS_VISIBLE_COUNT} more`}
          </Box>
        )}
      </>
    )
  }

  const databaseItems: Array<{
    text: string
    path: string
    icon: React.ReactNode
    isActive?: () => boolean
  }> = [
    { text: 'Databases', path: '/databases', icon: <DatabasesIcon /> },
    {
      text: 'Users',
      path: '/databases/users',
      icon: <UsersIcon />,
      isActive: () =>
        location.pathname === '/databases/users' ||
        location.pathname.startsWith('/databases/user/') ||
        location.pathname.startsWith('/databases/roles/'),
    },
  ]

  // ── Projects ─────────────────────────────────────────────────────────────────

  const activeProjectId = sidebarProjectIdStr ? Number(sidebarProjectIdStr) : null

  const sortedProjects = [...projects].sort((a: Project, b: Project) => {
    let cmp = 0
    switch (projectSortKey?.column) {
      case 'name':             cmp = a.name.localeCompare(b.name); break
      case 'updated_at':       cmp = new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime(); break
      case 'environment_count': cmp = a.environment_count - b.environment_count; break
      default:                 cmp = 0
    }
    return projectSortKey?.reverse ? -cmp : cmp
  })

  const visibleProjects = showAllProjects ? sortedProjects : sortedProjects.slice(0, PROJECTS_VISIBLE_COUNT)
  const hasMoreProjects = projects.length > PROJECTS_VISIBLE_COUNT

  const projectInitials = (name: string) => {
    const cleaned = (name || '').replace(/\s+/g, '')
    if (cleaned.length >= 2) return cleaned.slice(0, 2).toUpperCase()
    return (cleaned[0] || '?').toUpperCase()
  }

  const renderCompositeEnvGroup = (env: any, projId: number) => {
    const label = env.changeset_id || env.slug || `env-${env.id}`
    const envPath = `/project/${projId}/environment/${env.id}`
    const envActive =
      location.pathname.startsWith(envPath) ||
      (deploymentIdFromPath != null &&
        sidebarProjectIdStr != null &&
        sidebarEnvIdStr != null &&
        projId === Number(sidebarProjectIdStr) &&
        String(env.id) === String(sidebarEnvIdStr))

    if (collapsed) {
      return (
        <React.Fragment key={env.id}>
          <Tooltip title={label} placement="right">
            <ListItemButton
              sx={{ ...itemSx(envActive), justifyContent: 'center', px: 1 }}
              onClick={() => navigate(envPath)}
            >
              <ListItemIcon sx={iconSx(envActive)}><BranchIcon fontSize="small" /></ListItemIcon>
            </ListItemButton>
          </Tooltip>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment key={env.id}>
        <ListItemButton
          sx={{ ...itemSx(envActive, 0.375), pl: 4, justifyContent: 'flex-start' }}
          onClick={() => navigate(envPath)}
        >
          <ListItemIcon sx={iconSx(envActive, 26)}>
            <BranchIcon sx={{ fontSize: 13 }} />
          </ListItemIcon>
          <ListItemText
            primary={label}
            primaryTypographyProps={{ fontSize: '0.78rem', fontWeight: envActive ? 600 : 400, noWrap: true }}
          />
        </ListItemButton>
      </React.Fragment>
    )
  }

  const renderProjectList = () => {
    if (projects.length === 0) return null
    return (
      <>
        {visibleProjects.map((project) => {
          const isCurrentProject = activeProjectId === project.id
          const active = isCurrentProject && !sidebarEnvIdStr
          const path = `/project/${project.id}`

          const button = (
            <ListItemButton
              key={project.id}
              sx={{
                ...itemSx(active, 0.4),
                pl: collapsed ? 1 : 2.5,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : undefined,
              }}
              onClick={() => navigate(path)}
            >
              {collapsed ? (
                <Box
                  sx={{
                    width: 28,
                    height: 28,
                    borderRadius: 1,
                    bgcolor: active ? 'var(--palette-primary-400)' : 'rgba(255,255,255,0.15)',
                    color: active ? 'var(--palette-primary-0)' : 'rgba(255,255,255,0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '0.7rem',
                    fontWeight: 700,
                    letterSpacing: '0.02em',
                  }}
                >
                  {projectInitials(project.name)}
                </Box>
              ) : (
                <>
                  <ListItemIcon sx={iconSx(active)}><ProjectsIcon fontSize="small" /></ListItemIcon>
                  <ListItemText
                    primary={project.name}
                    primaryTypographyProps={{ fontSize: '0.8125rem', fontWeight: active ? 600 : 400, noWrap: true }}
                  />
                </>
              )}
            </ListItemButton>
          )

          const projectRow = collapsed ? (
            <Tooltip key={project.id} title={project.name} placement="right">{button}</Tooltip>
          ) : (
            <span key={project.id}>{button}</span>
          )

          if (isCurrentProject && projectEnvironments.length > 0) {
            return (
              <React.Fragment key={project.id}>
                {projectRow}
                {projectEnvironments.map(env => renderCompositeEnvGroup(env, project.id))}
              </React.Fragment>
            )
          }

          return projectRow
        })}

        {!collapsed && hasMoreProjects && (
          <Box
            onClick={() => setShowAllProjects(v => !v)}
            sx={{
              pl: 2.5,
              py: 0.25,
              cursor: 'pointer',
              color: 'rgba(255,255,255,0.5)',
              fontSize: '0.72rem',
              '&:hover': { color: 'rgba(255,255,255,0.82)' },
              userSelect: 'none',
            }}
          >
            {showAllProjects ? '↑ show less' : `+ ${projects.length - PROJECTS_VISIBLE_COUNT} more`}
          </Box>
        )}
      </>
    )
  }

  const userMenuOpen = Boolean(userMenuAnchor)
  const handleUserMenuOpen = (e: React.MouseEvent<HTMLElement>) => setUserMenuAnchor(e.currentTarget)
  const handleUserMenuClose = () => setUserMenuAnchor(null)
  const handleLogout = () => {
    handleUserMenuClose()
    logout()
  }
  const isJoram = user?.login === 'joram'

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box
      data-testid="sidebar"
      sx={{
        width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH,
        flexShrink: 0,
        bgcolor: 'var(--palette-primary-75)',
        borderRight: '1px solid rgba(255,255,255,0.07)',
        display: { xs: 'none', md: 'flex' },
        flexDirection: 'column',
        pt: 0.5,
        pb: 1,
        px: collapsed ? 0.5 : 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        transition: 'width 0.2s ease',
      }}
    >
      {/* Logo at top — centered; square icon when collapsed */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          py: 1,
          mb: 0.5,
          borderBottom: '1px solid rgba(255,255,255,0.07)',
        }}
      >
        <ListItemButton
          component="a"
          href="/"
          onClick={(e) => {
            e.preventDefault()
            navigate('/')
          }}
          sx={{
            p: 0,
            minHeight: 'auto',
            '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
            borderRadius: 1,
            justifyContent: 'center',
          }}
        >
          <VeilStreamLogo
            variant={collapsed ? 'icon' : 'full'}
            size="small"
            sx={collapsed ? { height: 28, width: 28 } : { height: 28, width: 96 }}
          />
        </ListItemButton>
      </Box>
      <List dense disablePadding sx={{ flex: 1 }}>

        {/* ── Projects; Domains follows project list when expanded ── */}
        <SectionLabel label="Projects" />
        {renderItem('Projects', <ProjectsIcon />, isProjectsExact, () => navigate('/projects'))}
        {showProjectTree && renderProjectList()}
        {showDomainsNav &&
          renderItem('Domains', <DomainsIcon />, isDomainsActive, () => navigate('/domains'))}

        <Divider sx={{ my: 1, borderColor: 'rgba(255,255,255,0.07)' }} />

        {/* Data sources */}
        <SectionLabel label="Data sources" />
        {renderItem('Databases', <DatabasesIcon />, isDatabasesListActive, () => navigate('/databases'))}
        {showDatabaseInstanceList && renderDatabaseList()}
        {databaseItems
          .filter((item) => item.path !== '/databases')
          .map((item) => (
            <React.Fragment key={item.path}>
              {renderItem(
                item.text,
                item.icon,
                item.isActive ? item.isActive() : isDatabaseActive(item.path),
                () => navigate(item.path),
                1,
                '0.875rem',
              )}
              {item.path === '/databases/users' && showDatabaseUserList && renderDatabaseUserList()}
            </React.Fragment>
          ))}

      </List>

      {/* Account + collapse in one section: row when expanded, stacked when collapsed */}
      {user && (
        <Box
          sx={{
            borderTop: '1px solid rgba(255,255,255,0.07)',
            pt: 1,
            pb: 0.5,
            px: collapsed ? 0 : 0.5,
            display: 'flex',
            flexDirection: collapsed ? 'column' : 'row',
            alignItems: collapsed ? 'center' : 'center',
            justifyContent: collapsed ? 'center' : 'space-between',
            gap: collapsed ? 0.5 : 0,
          }}
        >
          <Tooltip title={user.name || user.login} placement="right">
            <IconButton
              aria-label="user-profile"
              onClick={handleUserMenuOpen}
              sx={{
                flexShrink: 0,
                borderRadius: 1,
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
              }}
            >
              {user.avatar_url ? (
                <Avatar src={user.avatar_url} alt={user.name || user.login} sx={{ width: 32, height: 32 }} />
              ) : (
                <AccountIcon sx={{ fontSize: 28 }} />
              )}
              {!collapsed && (
                <Typography variant="body2" noWrap sx={{ ml: 1.5, color: 'inherit', maxWidth: 120 }} component="span">
                  {user.name || user.login}
                </Typography>
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <IconButton
              size="small"
              onClick={toggleCollapsed}
              sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255,255,255,0.8)' }, flexShrink: 0 }}
            >
              {collapsed ? <ExpandIcon fontSize="small" /> : <CollapseIcon fontSize="small" />}
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={userMenuAnchor}
            open={userMenuOpen}
            onClose={handleUserMenuClose}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          >
            <MenuItem disabled>
              <Typography variant="body2">{user.name || user.login}</Typography>
            </MenuItem>
            {isJoram && (
              <MenuItem
                onClick={() => {
                  handleUserMenuClose()
                  window.open('https://admin.veilstream.com', '_blank', 'noopener,noreferrer')
                }}
              >
                <ListItemIcon><AdminPanelSettings fontSize="small" /></ListItemIcon>
                Admin
              </MenuItem>
            )}
            <MenuItem onClick={() => { handleUserMenuClose(); navigate('/settings') }}>
              <ListItemIcon><PaletteIcon fontSize="small" /></ListItemIcon>
              Settings
            </MenuItem>
            {isJoram && (
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/billing') }}>
                <ListItemIcon><ReceiptIcon fontSize="small" /></ListItemIcon>
                Billing
              </MenuItem>
            )}
            <MenuItem component="a" href="https://docs.veilstream.com" target="_blank" rel="noopener noreferrer" onClick={handleUserMenuClose}>
              <ListItemIcon><MenuBookIcon fontSize="small" /></ListItemIcon>
              Documentation
            </MenuItem>
            <MenuItem onClick={() => { handleUserMenuClose(); navigate('/feedback') }}>
              <ListItemIcon><FeedbackIcon fontSize="small" /></ListItemIcon>
              Feedback
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      )}
    </Box>
  )
}

export default Sidebar
